import has from "lodash/has";

import { queryClient, QueryKeys } from "@/api/core";
import { type Alert } from "@/schemas";

import {
  hasPages,
  isEntity,
  type PaginatedResult,
  removePageItem,
} from "./queries";

import type { AlertAccount } from "@/schemas/alert";
import type { InfiniteData } from "@tanstack/react-query";

enum AlertFilterTypes {
  ALL = "all",
  MENTIONS = "mentions",
  REACTIONS = "reactions",
  REBLOGS = "reblogs",
  FOLLOWS = "follows",
  SUBSCRIPTIONS = "subscriptions",
  ADMINISTRATION = "administration",
}

enum AlertTypes {
  REACTIONS = "reactions",
  FOLLOWS = "follows",
  GROUP_PROMOTION = "group_promotion",
  GROUP_DEMOTION = "group_demotion",
  GROUP_REQUESTS = "group_requests",
  MARKETING_MESSAGE = "marketing_message",
  MENTION = "mention",
  POLL_CLOSE = "poll_close",
  // POLL_VOTES = 'poll_votes',
  QUOTE = "quote",
  REBLOGS = "reblogs",
  REPLY = "reply",
  SUBSCRIBED_STATUS = "subscribed_status",
  TV_REMINDER = "tv_reminder",
  BOOST_SUBMITTED = "boost_submitted",
  BOOST_APPROVED = "boost_approved",
  BOOST_REJECTED = "boost_rejected",
  BOOST_COMPLETED = "boost_completed",
  BOOST_CANCELLED = "boost_cancelled",
  BOOST_PAYMENT_FAILED = "boost_payment_failed",
}

type AlertType = keyof typeof AlertTypes;

/** Ensure the Alert is a valid, known type. */
const validType = (type: string): type is AlertTypes =>
  Object.values(AlertTypes).includes(type as AlertTypes);

const hasStatusId = (data: unknown): data is { status: { id: string } } => {
  if (typeof data !== "object" || data === null || !("status" in data)) {
    return false;
  }
  const status = (data as { status: unknown }).status;
  return typeof status === "object" && status !== null && "id" in status;
};

type AlertWithStatus = { status?: { id: string } };

const removeAlertsRelatedToStatus = (statusId: string) => {
  queryClient
    .getQueriesData<unknown>({ queryKey: QueryKeys.alerts.global })
    .forEach(([cachedKey, cachedData]) => {
      if (hasPages(cachedData)) {
        // infinite pages
        removePageItem<AlertWithStatus>(
          cachedKey,
          {} as AlertWithStatus,
          (alert) => alert.status?.id === statusId,
        );
      } else if (Array.isArray(cachedData)) {
        // basic arrays
        queryClient.setQueryData<AlertWithStatus[]>(
          cachedKey,
          (cachedData as AlertWithStatus[]).filter(
            (entity) => entity.status?.id !== statusId,
          ),
        );
      } else if (hasStatusId(cachedData) && cachedData.status.id === statusId) {
        // single entities
        queryClient.removeQueries({ queryKey: cachedKey, exact: true });
        queryClient.cancelQueries({ queryKey: cachedKey, exact: true });
      }
    });
};

const removeAlertsRelatedToAccount = (accountId: string) => {
  function editOrRemoveAlert(alert: Alert): Alert | null {
    const newAlert = { ...alert } as Alert;

    if (newAlert.accounts?.some((account) => account.id === accountId)) {
      // if there is more accounts, we want to remove this account, but if it's the only one, remove alert
      if (newAlert.accounts.length > 1) {
        newAlert.accounts = newAlert.accounts.filter(
          (account) => account.id !== accountId,
        );
        newAlert.total_count = newAlert.total_count - 1;
        return newAlert;
      }
      return null;
    }
    // if status is related to account, remove alert
    if (newAlert.status?.account.id === accountId) {
      return null;
    }

    return newAlert;
  }

  queryClient
    .getQueriesData<unknown>({ queryKey: QueryKeys.alerts.global })
    .forEach(([cachedKey, cachedData]) => {
      if (hasPages(cachedData)) {
        // infinite pages
        const infinite = cachedData as InfiniteData<
          PaginatedResult<Alert | AlertAccount>
        >;
        const pages = infinite.pages.map((page) => {
          const result: Array<Alert | AlertAccount> = page.result
            .map((item): Alert | AlertAccount | null => {
              // check alert accounts query
              if (has(item, "acct")) {
                if (item.id === accountId) return null;
                return item;
              }
              // alerts
              return editOrRemoveAlert(item as Alert);
            })
            .filter((alert): alert is Alert | AlertAccount => alert !== null);

          return { ...page, result };
        });
        queryClient.setQueryData(cachedKey, { ...infinite, pages });
      } else if (Array.isArray(cachedData)) {
        // basic arrays
        const next: Alert[] = (cachedData as Alert[])
          .map((alert) => editOrRemoveAlert(alert))
          .filter((alert): alert is Alert => alert !== null);

        queryClient.setQueryData(cachedKey, next);
        // single entities
      } else if (isEntity(cachedData)) {
        const newAlert = editOrRemoveAlert(cachedData as Alert);
        if (newAlert !== null) {
          queryClient.setQueryData(cachedKey, newAlert);
          return;
        }
        queryClient.removeQueries({ queryKey: cachedKey, exact: true });
        queryClient.cancelQueries({ queryKey: cachedKey, exact: true });
      }
    });
};

export {
  AlertTypes,
  type AlertType,
  AlertFilterTypes,
  validType,
  removeAlertsRelatedToStatus,
  removeAlertsRelatedToAccount,
};
